import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../api/client";
import { CheckCircle, ChevronLeft, ChevronRight, Sparkles, CreditCard } from "lucide-react";

/* Online-client intake — every field the AI uses to build a deeply personal program.
   Kept as a flat object so it maps 1:1 to the backend NutritionProfileIn intake fields. */
const blankIntake = {
  // Personal
  age: "",
  sex: "",
  height_cm: "",
  weight_kg: "",
  // Lifestyle
  family_situation: "",
  work_situation: "",
  stress_level: "",
  sleep_hours: "",
  sleep_quality: "",
  // Training
  training_background: "",
  training_history: "",
  training_days_per_week: "",
  training_location: "",
  equipment_list: "",
  // Health
  conditions: "",
  injuries: "",
  menopause: false,
  // Goal & psychology
  primary_goal: "",
  goal: "",
  past_obstacles: "",
  learning_style: "",
  support_style: "",
  // Food & habits
  food_relationship: "",
  bad_habits: "",
  allergies: "",
};

const STEP_IDS = ["plan", "account", "about", "training", "goals", "pay"];

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const [params] = useSearchParams();
  const [stepIdx, setStepIdx] = useState(0);
  const [planType, setPlanType] = useState(""); // "" | "existing" | "online"
  const [account, setAccount] = useState({ name: "", email: "", password: "" });
  const [intake, setIntake] = useState({ ...blankIntake });
  const [reason, setReason] = useState(""); // existing-client "tell Sopfie who you are"
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { message }

  const STEPS = STEP_IDS.map((id) => ({ id, label: t(`register.step.${id}`) }));

  // If the URL says ?plan=existing or ?plan=online, pre-select and skip the first step.
  useEffect(() => {
    const p = params.get("plan");
    if ((p === "existing" || p === "online") && !planType) {
      setPlanType(p);
      setStepIdx(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For the "existing" flow, we skip all intake steps — they come to 'account' then submit.
  const visibleSteps = planType === "online"
    ? STEPS
    : planType === "existing"
      ? [STEPS[0], STEPS[1], { id: "done", label: t("register.step.done") }]
      : [STEPS[0]];

  const step = visibleSteps[stepIdx];

  const canProceed = () => {
    if (step.id === "plan") return !!planType;
    if (step.id === "account") {
      return account.name.trim() && account.email.trim() && account.password.length >= 6;
    }
    if (step.id === "about") return !!intake.age && !!intake.sex;
    if (step.id === "training") return !!intake.training_days_per_week && !!intake.training_location;
    if (step.id === "goals") return !!intake.primary_goal;
    return true;
  };

  const next = () => { setError(""); if (canProceed()) setStepIdx((i) => Math.min(i + 1, visibleSteps.length - 1)); };
  const back = () => { setError(""); setStepIdx((i) => Math.max(i - 1, 0)); };

  // Clean the intake: drop blanks so backend doesn't coerce empty strings.
  const cleanIntake = () => {
    const out = {};
    Object.entries(intake).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (["age", "training_days_per_week"].includes(k)) {
        const n = parseInt(v, 10);
        if (Number.isFinite(n)) out[k] = n;
        return;
      }
      if (["height_cm", "weight_kg", "sleep_hours"].includes(k)) {
        const n = parseFloat(v);
        if (Number.isFinite(n)) out[k] = n;
        return;
      }
      out[k] = v;
    });
    return out;
  };

  // Submit path — existing clients
  const submitExisting = async () => {
    setError(""); setBusy(true);
    try {
      const res = await register(account.email, account.password, account.name);
      if (!res.ok) setError(res.error);
      else setPending({ message: res.message || "Thank you. Sopfie will review your request and approve your access." });
    } finally {
      setBusy(false);
    }
  };

  // Submit path — online clients (go to Stripe)
  const submitOnline = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/payments/online-signup/checkout", {
        email: account.email.trim(),
        password: account.password,
        name: account.name.trim(),
        origin_url: window.location.origin,
        intake: { ...cleanIntake(), goal: reason || cleanIntake().goal },
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Could not start the checkout. Please try again.");
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="register-page">
        <div className="max-w-xl mx-auto px-6 md:px-10">
          <div className="card-soft p-8 mt-8 text-center" data-testid="register-pending">
            <CheckCircle size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--accent)]" />
            <h2 className="font-serif text-2xl text-[color:var(--ink)] mt-4">{t("register.pending.title")}</h2>
            <p className="mt-3 text-[color:var(--ink-2)] leading-relaxed">{pending.message}</p>
            <p className="mt-2 text-sm text-[color:var(--ink-3)]">{t("register.pending.tagline")}</p>
            <div className="mt-7 flex justify-center gap-3">
              <Link to="/" className="btn-ghost">{t("register.pending.back")}</Link>
              <Link to="/login" className="btn-primary">{t("register.pending.signin")}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="register-page">
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        <p className="overline mb-3">{t("register.overline")}</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">{t("register.title")}</h1>
        <p className="mt-3 text-[color:var(--ink-2)]">{t("register.subtitle")}</p>

        {/* Step bar */}
        <div className="mt-8 flex items-center gap-1.5 overflow-x-auto pb-2" data-testid="register-stepbar">
          {visibleSteps.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1.5 shrink-0">
              <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border whitespace-nowrap ${i === stepIdx
                ? "bg-[color:var(--accent)] text-[var(--bg)] border-[color:var(--accent)]"
                : i < stepIdx
                  ? "bg-[color:var(--accent)]/10 text-[color:var(--accent)] border-[color:var(--accent)]/20"
                  : "bg-transparent text-[color:var(--ink-3)] border-[color:var(--line-soft)]"
              }`}>
                {i + 1}. {s.label}
              </span>
              {i < visibleSteps.length - 1 && <span className="text-[color:var(--ink-3)]">·</span>}
            </div>
          ))}
        </div>

        <div className="card-soft p-7 sm:p-8 mt-6 space-y-5">
          {/* Step: plan */}
          {step.id === "plan" && (
            <div data-testid="register-step-plan">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.plan.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.plan.sub")}</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => { setPlanType("existing"); setStepIdx(1); }}
                  className={`text-left p-5 rounded-2xl border transition-all ${planType === "existing" ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-[color:var(--line-soft)] hover:border-[color:var(--accent)]/40"}`}
                  data-testid="register-plan-existing"
                >
                  <p className="overline text-[color:var(--accent)]">{t("register.plan.existing.tag")}</p>
                  <p className="font-serif text-xl text-[color:var(--ink)] mt-2">{t("register.plan.existing.title")}</p>
                  <p className="text-sm text-[color:var(--ink-2)] mt-2">{t("register.plan.existing.body")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => { setPlanType("online"); setStepIdx(1); }}
                  className={`text-left p-5 rounded-2xl border transition-all ${planType === "online" ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-[color:var(--line-soft)] hover:border-[color:var(--accent)]/40"}`}
                  data-testid="register-plan-online"
                >
                  <p className="overline text-[color:var(--accent)]">{t("register.plan.online.tag")}</p>
                  <p className="font-serif text-xl text-[color:var(--ink)] mt-2">{t("register.plan.online.title")}</p>
                  <p className="text-sm text-[color:var(--ink-2)] mt-2">{t("register.plan.online.body")}</p>
                </button>
              </div>
            </div>
          )}

          {/* Step: account */}
          {step.id === "account" && (
            <div data-testid="register-step-account">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.account.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.account.sub")}</p>
              <div className="mt-5 space-y-3">
                <div>
                  <label className="overline block mb-2">{t("common.name")}</label>
                  <input className="input-warm" value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} data-testid="register-name" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("common.email")}</label>
                  <input type="email" className="input-warm" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} data-testid="register-email" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.account.password_hint")}</label>
                  <input type="password" className="input-warm" minLength={6} value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} data-testid="register-password" />
                </div>
                {planType === "existing" && (
                  <div>
                    <label className="overline block mb-2">{t("register.account.reason_label")}</label>
                    <textarea rows={3} className="input-warm" placeholder={t("register.account.reason_ph")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="register-reason" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step: about (online only) */}
          {step.id === "about" && (
            <div data-testid="register-step-about">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.about.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.about.sub")}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t("register.about.age")}</label>
                  <input type="number" className="input-warm" value={intake.age} onChange={(e) => setIntake({ ...intake, age: e.target.value })} data-testid="intake-age" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.sex")}</label>
                  <select className="input-warm" value={intake.sex} onChange={(e) => setIntake({ ...intake, sex: e.target.value })} data-testid="intake-sex">
                    <option value="">—</option>
                    <option value="female">{t("register.about.sex.female")}</option>
                    <option value="male">{t("register.about.sex.male")}</option>
                    <option value="other">{t("register.about.sex.other")}</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.height")}</label>
                  <input type="number" className="input-warm" value={intake.height_cm} onChange={(e) => setIntake({ ...intake, height_cm: e.target.value })} data-testid="intake-height" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.weight")}</label>
                  <input type="number" className="input-warm" value={intake.weight_kg} onChange={(e) => setIntake({ ...intake, weight_kg: e.target.value })} data-testid="intake-weight" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.about.family")}</label>
                  <input className="input-warm" placeholder={t("register.about.family_ph")} value={intake.family_situation} onChange={(e) => setIntake({ ...intake, family_situation: e.target.value })} data-testid="intake-family" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.about.work")}</label>
                  <input className="input-warm" placeholder={t("register.about.work_ph")} value={intake.work_situation} onChange={(e) => setIntake({ ...intake, work_situation: e.target.value })} data-testid="intake-work" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.stress")}</label>
                  <select className="input-warm" value={intake.stress_level} onChange={(e) => setIntake({ ...intake, stress_level: e.target.value })} data-testid="intake-stress">
                    <option value="">—</option>
                    <option value="low">{t("register.about.stress.low")}</option>
                    <option value="medium">{t("register.about.stress.medium")}</option>
                    <option value="high">{t("register.about.stress.high")}</option>
                    <option value="burnout">{t("register.about.stress.burnout")}</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.sleep_quality")}</label>
                  <select className="input-warm" value={intake.sleep_quality} onChange={(e) => setIntake({ ...intake, sleep_quality: e.target.value })} data-testid="intake-sleep-quality">
                    <option value="">—</option>
                    <option value="poor">{t("register.about.sleep_quality.poor")}</option>
                    <option value="okay">{t("register.about.sleep_quality.okay")}</option>
                    <option value="good">{t("register.about.sleep_quality.good")}</option>
                    <option value="excellent">{t("register.about.sleep_quality.excellent")}</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.about.sleep_hours")}</label>
                  <input type="number" step="0.5" className="input-warm" value={intake.sleep_hours} onChange={(e) => setIntake({ ...intake, sleep_hours: e.target.value })} data-testid="intake-sleep-hours" />
                </div>
                <div>
                  <label className="overline block mb-2 flex items-center gap-2">
                    <input type="checkbox" checked={intake.menopause} onChange={(e) => setIntake({ ...intake, menopause: e.target.checked })} data-testid="intake-menopause" /> {t("register.about.menopause")}
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.about.conditions")}</label>
                  <textarea rows={2} className="input-warm" placeholder={t("register.about.conditions_ph")} value={intake.conditions} onChange={(e) => setIntake({ ...intake, conditions: e.target.value })} data-testid="intake-conditions" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.about.injuries")}</label>
                  <textarea rows={2} className="input-warm" placeholder={t("register.about.injuries_ph")} value={intake.injuries} onChange={(e) => setIntake({ ...intake, injuries: e.target.value })} data-testid="intake-injuries" />
                </div>
              </div>
            </div>
          )}

          {/* Step: training */}
          {step.id === "training" && (
            <div data-testid="register-step-training">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.training.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.training.sub")}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.training.bg")}</label>
                  <input className="input-warm" placeholder={t("register.training.bg_ph")} value={intake.training_background} onChange={(e) => setIntake({ ...intake, training_background: e.target.value })} data-testid="intake-bg" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.training.history")}</label>
                  <textarea rows={3} className="input-warm" placeholder={t("register.training.history_ph")} value={intake.training_history} onChange={(e) => setIntake({ ...intake, training_history: e.target.value })} data-testid="intake-history" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.training.days")}</label>
                  <select className="input-warm" value={intake.training_days_per_week} onChange={(e) => setIntake({ ...intake, training_days_per_week: e.target.value })} data-testid="intake-days">
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.training.location")}</label>
                  <select className="input-warm" value={intake.training_location} onChange={(e) => setIntake({ ...intake, training_location: e.target.value })} data-testid="intake-location">
                    <option value="">—</option>
                    <option value="gym">{t("register.training.location.gym")}</option>
                    <option value="home">{t("register.training.location.home")}</option>
                    <option value="both">{t("register.training.location.both")}</option>
                    <option value="outdoor">{t("register.training.location.outdoor")}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.training.equipment")}</label>
                  <textarea rows={2} className="input-warm" placeholder={t("register.training.equipment_ph")} value={intake.equipment_list} onChange={(e) => setIntake({ ...intake, equipment_list: e.target.value })} data-testid="intake-equipment" />
                </div>
              </div>
            </div>
          )}

          {/* Step: goals & support */}
          {step.id === "goals" && (
            <div data-testid="register-step-goals">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.goals.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.goals.sub")}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.goals.goal")}</label>
                  <input className="input-warm" placeholder={t("register.goals.goal_ph")} value={intake.primary_goal} onChange={(e) => setIntake({ ...intake, primary_goal: e.target.value })} data-testid="intake-goal" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.goals.obstacles")}</label>
                  <textarea rows={3} className="input-warm" placeholder={t("register.goals.obstacles_ph")} value={intake.past_obstacles} onChange={(e) => setIntake({ ...intake, past_obstacles: e.target.value })} data-testid="intake-obstacles" />
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.goals.learning")}</label>
                  <select className="input-warm" value={intake.learning_style} onChange={(e) => setIntake({ ...intake, learning_style: e.target.value })} data-testid="intake-learning">
                    <option value="">—</option>
                    <option value="visual">{t("register.goals.learning.visual")}</option>
                    <option value="verbal">{t("register.goals.learning.verbal")}</option>
                    <option value="hands_on">{t("register.goals.learning.hands_on")}</option>
                    <option value="analytical">{t("register.goals.learning.analytical")}</option>
                    <option value="mixed">{t("register.goals.learning.mixed")}</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t("register.goals.food")}</label>
                  <input className="input-warm" placeholder={t("register.goals.food_ph")} value={intake.food_relationship} onChange={(e) => setIntake({ ...intake, food_relationship: e.target.value })} data-testid="intake-food" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.goals.habits")}</label>
                  <textarea rows={2} className="input-warm" placeholder={t("register.goals.habits_ph")} value={intake.bad_habits} onChange={(e) => setIntake({ ...intake, bad_habits: e.target.value })} data-testid="intake-habits" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.goals.support")}</label>
                  <textarea rows={3} className="input-warm" placeholder={t("register.goals.support_ph")} value={intake.support_style} onChange={(e) => setIntake({ ...intake, support_style: e.target.value })} data-testid="intake-support" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2">{t("register.goals.allergies")}</label>
                  <input className="input-warm" value={intake.allergies} onChange={(e) => setIntake({ ...intake, allergies: e.target.value })} data-testid="intake-allergies" />
                </div>
              </div>
            </div>
          )}

          {/* Step: pay (online only) */}
          {step.id === "pay" && (
            <div data-testid="register-step-pay">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[color:var(--accent)]/15 flex items-center justify-center shrink-0">
                  <Sparkles size={20} strokeWidth={1.4} className="text-[color:var(--accent)]" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.pay.title")}</h2>
                  <p className="text-sm text-[color:var(--ink-2)]">{t("register.pay.sub")}</p>
                </div>
              </div>
              <div className="mt-6 rounded-2xl border border-[color:var(--line-soft)] p-5 bg-[var(--bg)]">
                <div className="flex items-baseline justify-between">
                  <p className="font-serif text-xl text-[color:var(--ink)]">{t("register.pay.plan_name")}</p>
                  <p className="font-serif text-3xl text-[color:var(--ink)]"><em className="not-italic text-[color:var(--accent)]">650</em> <span className="text-sm text-[color:var(--ink-3)]">{t("register.pay.per_month")}</span></p>
                </div>
                <ul className="text-sm text-[color:var(--ink-2)] mt-4 space-y-1.5 list-disc pl-5">
                  <li>{t("register.pay.bullet1")}</li>
                  <li>{t("register.pay.bullet2")}</li>
                  <li>{t("register.pay.bullet3")}</li>
                  <li>{t("register.pay.bullet4")}</li>
                </ul>
                <p className="text-xs text-[color:var(--ink-3)] mt-4">
                  {t("register.pay.footnote")}
                </p>
              </div>
              {error && <p className="mt-4 text-sm text-[color:var(--accent-2)] bg-[color:var(--bg-2)] border border-[color:var(--line-soft)] rounded-lg px-3 py-2" data-testid="register-error">{error}</p>}
              <button
                onClick={submitOnline}
                disabled={busy}
                className="btn-primary mt-5 w-full justify-center"
                data-testid="register-pay-submit"
              >
                <CreditCard size={16} strokeWidth={1.6} /> {busy ? t("register.pay.opening") : t("register.pay.submit")}
              </button>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-3 text-center">
                {t("register.pay.secure")}
              </p>
            </div>
          )}

          {/* Step: existing done */}
          {step.id === "done" && (
            <div data-testid="register-step-done">
              <h2 className="font-serif text-2xl text-[color:var(--ink)]">{t("register.done.title")}</h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("register.done.sub")}</p>
              <div className="mt-5 rounded-2xl border border-[color:var(--line-soft)] p-5 bg-[var(--bg)] space-y-1">
                <p className="text-sm"><span className="overline mr-2">{t("common.name")}</span>{account.name}</p>
                <p className="text-sm"><span className="overline mr-2">{t("common.email")}</span>{account.email}</p>
                {reason && <p className="text-sm mt-2 text-[color:var(--ink-2)] italic">"{reason}"</p>}
              </div>
              {error && <p className="mt-4 text-sm text-[color:var(--accent-2)]" data-testid="register-error">{error}</p>}
              <button onClick={submitExisting} disabled={busy} className="btn-primary mt-5 w-full justify-center" data-testid="register-submit">
                {busy ? t("register.done.sending") : t("register.done.submit")}
              </button>
            </div>
          )}

          {/* Navigation */}
          {step.id !== "plan" && step.id !== "pay" && step.id !== "done" && (
            <div className="flex items-center justify-between pt-2">
              <button onClick={back} className="btn-ghost !py-1.5 !px-3 !text-sm" data-testid="register-back">
                <ChevronLeft size={14} /> {t("common.back")}
              </button>
              <button onClick={next} disabled={!canProceed()} className="btn-primary !py-1.5 !px-4 !text-sm" data-testid="register-next">
                {t("common.continue")} <ChevronRight size={14} />
              </button>
            </div>
          )}
          {(step.id === "pay" || step.id === "done") && (
            <div>
              <button onClick={back} className="btn-ghost !py-1.5 !px-3 !text-sm" data-testid="register-back">
                <ChevronLeft size={14} /> {t("common.back")}
              </button>
            </div>
          )}
        </div>

        <p className="text-sm text-[color:var(--ink-3)] mt-6">
          {t("register.already")}{" "}
          <Link to="/login" className="underline underline-offset-4 hover:text-[color:var(--accent)]">{t("register.already.signin")}</Link>
        </p>
      </div>
    </div>
  );
}
